import { Box, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, styled, tableCellClasses } from "@mui/material"
import { FormBuilder } from "components/FormBuilder/FormBuilder"
import React, { useMemo } from 'react'
import { useFormState, useWatch } from "react-hook-form"
import { useTranslation } from "react-i18next"
import useColors from "utils/useColors/useColors"
import { getCardTypeIcon } from "utils/utils"
import { percentageFormatValidator } from "utils/formValidation"

const ProcessorTableCell = styled(TableCell)(() => ({
  [`&.${tableCellClasses.head}`]: {
    color: "white",
  },
  width: 0, // Hack to force equal column grow
  padding: "16px 24px",
}))

/**
 * @typedef {"VISA"|"MASTER"|"UNIONPAY"|"JCB"|"AMEX"|"OCTOPUS"|"WECHAT"|"ALIPAY"|"PAYME"} processorCardType
 * @typedef {"ALLINPAY"|"FUBON"|"YEDPAY"|"AMEX_BBMSL"|"AMEX"|"OCTOPUS"|"OCTOPUS_BBMSL"|"OCTOPUS_HKBN"|"WECHAT_PAY"|"ALIPAY"|"HSBC"|"NONE"} processorPaymentGateway
 * @typedef {"domesticMdr"|"foreignMdr"|"inUse"|"referrerCommission"} processorCardTypeFields
 * @typedef {"businessPlan"|"enabled"|"mcc"} processorGatewayFields
 * 
 * @typedef {"sme"|"normal"} processorBusinessPlanOption
 * 
 * @typedef {Object} processorGatewayDefaultOptions
 * @property {processorBusinessPlanOption} businessPlan
 * @property {boolean} enabled
 * @property {string} mcc
 * 
 * @typedef {Object} processorGatewaySpecialHandling
 * @property {(value: any, fieldProps: import("components/FormBuilder/lib/FormBuilderFieldBuilder").formPropsType) => void} enabledOnCheckFn
 * 
 * @typedef {Object} processorGatewayConfig
 * @property {processorPaymentGateway} gatewayKey
 * @property {processorGatewayFields[]} [unavailableFields]
 * @property {processorGatewayDefaultOptions} [defaultValues]
 * @property {processorGatewaySpecialHandling} [specialHandling]
 * 
 * @typedef {Object} processorCardTypeDefaultOptions
 * @property {string} domesticMdr
 * @property {string} foreignMdr
 * @property {string} referrerCommission
 * @property {processorPaymentGateway} inUse
 * 
 * @typedef {Object} processorCardTypeConfig
 * @property {processorCardType} cardTypeKey
 * @property {processorCardTypeFields[]} [unavailableFields]
 * @property {processorCardTypeDefaultOptions} [defaultValues]
 * @property {processorGatewayConfig[]} gateways
 * @property {string[]} [specialHandling]
 */

/**
 * Processor form value type
 * @typedef {object} processorFormGatewayObject
 * @property {processorBusinessPlanOption} businessPlan
 * @property {boolean} enabled
 * @property {string} mcc
 * 
 * @typedef {object} processorFormCardTypeObject
 * @property {string} domesticMdr
 * @property {string} foreignMdr
 * @property {string} referrerCommission
 * @property {processorPaymentGateway} inUse
 * @property {Record<processorPaymentGateway, processorFormGatewayObject>} gateways
 * 
 * @typedef {Record<processorCardType, processorFormCardTypeObject>} processorFormValue
 */

const UnavailablePlaceholder = () => <Box>-</Box>

const cardTypeCellWidth = "150px";
const cellWidth = "150px";
const wideCellWidth = "300px";
const GATEWAY_INUSE_NONE_VALUE = "NONE";
const COMPONENT_LOCALE_KEY = "component.form.processorTable";

const hasError = (errors, fieldName) =>
  Boolean(fieldName.split(".").reduce((value, key) => value?.[key], errors));

/**
 * @param {Object} props 
 * @param {import("components/FormBuilder/lib/FormBuilderFieldBuilder").formPropsType} props.formProps
 * @param {processorCardTypeConfig} props.config
 * @param {any[]} props.mccOptions
 * @param {boolean} props.disabled
 */
const ProcessorTable = ({ formProps, config, mccOptions, disabled }) => {
  const { t } = useTranslation();
  const c = useColors();

  const BUSINESS_PLAN_OPTIONS = useMemo(() => [
    { key: "sme", title: "SME", value: "sme" },
    { key: "normal", title: "Standard", value: "normal" },
  ], []);

  const formKey = useMemo(() => `processors.${config.cardTypeKey}`, [config.cardTypeKey])
  const domesticMdrFieldName = `${formKey}.domesticMdr`;
  const foreignMdrFieldName = `${formKey}.foreignMdr`;
  const { errors } = useFormState({
    control: formProps.control,
    name: [domesticMdrFieldName, foreignMdrFieldName],
  });

  /** @type {Record<processorPaymentGateway, processorFormGatewayObject>} */
  const formGatewayValues = useWatch({
    control: formProps.control,
    name: `${formKey}.gateways`,
  });
  const selectableGateways = [...Object.entries(formGatewayValues ?? {}).map(([key, value]) => {
    if (value.enabled) return key;
  }), GATEWAY_INUSE_NONE_VALUE].filter(Boolean);

  const selectedGateway = useWatch({ control: formProps.control, name: `${formKey}.inUse` });
  const gatewayIsSelected = selectedGateway !== GATEWAY_INUSE_NONE_VALUE;

  // Default values from previous steps or initial values, only applied for the first time
  const defaultValues = useMemo(() => {
    const formValues = formProps.getValues();
    return ({
      inUse: selectableGateways[0],
      mcc: formValues.merchantCategoryCode,
    })
  }, []);

  const percentageRule = {
    rule: (value) => percentageFormatValidator(value),
    errorMessage: t("component.form.validation.percentageRange"),
  };

  /** @type {Record<string, import("components/FormBuilder/lib/FormBuilderFieldBuilder").formBuilderFieldConfig>} */
  const tableFields = {
    domesticMdr: {
      fieldType: "textInput",
      fieldName: domesticMdrFieldName,
      hideLabel: "removePadding",
      noErrorMessage: !hasError(errors, domesticMdrFieldName),
      mandatory: gatewayIsSelected,
      disabled: disabled,
      config: {
        inputType: "percentage",
        numberRange: [0, 100],
        defaultValue: config.defaultValues?.domesticMdr,
        customRule: percentageRule,
      }
    },
    foreignMdr: {
      fieldType: "textInput",
      fieldName: foreignMdrFieldName,
      hideLabel: "removePadding",
      noErrorMessage: !hasError(errors, foreignMdrFieldName),
      mandatory: gatewayIsSelected,
      disabled: disabled,
      config: {
        inputType: "percentage",
        numberRange: [0, 100],
        defaultValue: config.defaultValues?.foreignMdr,
        customRule: percentageRule,
      }
    },
    referrerCommission: {
      fieldType: "textInput",
      fieldName: `${formKey}.referrerCommission`,
      hideLabel: "removePadding",
      noErrorMessage: true,
      mandatory: gatewayIsSelected,
      disabled: disabled,
      config: {
        inputType: "percentage",
        numberRange: [0, 100],
        defaultValue: config.defaultValues?.referrerCommission
      }
    },
    inUse: {
      fieldType: "selection",
      fieldName: `${formKey}.inUse`,
      hideLabel: "removePadding",
      noErrorMessage: true,
      mandatory: true,
      disabled: disabled,
      config: {
        defaultValue: config.defaultValues?.inUse || defaultValues.inUse,
        options: selectableGateways.map((gateway) => ({ key: gateway, title: gateway, value: gateway })),
      }
    },
  }

  return (
    <TableContainer component={Paper} sx={{
      border: `1px solid e0e0e0`,
      boxShadow: "0 1px 3px rgba(0, 0, 0, 0.1)",
      overflowX: "auto"
    }}>
      <Table>
        <TableHead sx={{ backgroundColor: c.brandDark25, color: "white" }}>
          <TableRow>
            <ProcessorTableCell sx={{ minWidth: cardTypeCellWidth }}>
              {t(`${COMPONENT_LOCALE_KEY}.columns.cardType`)}
            </ProcessorTableCell>
            <ProcessorTableCell sx={{ minWidth: cellWidth }}>
              {t(`${COMPONENT_LOCALE_KEY}.columns.domesticMdr`)}
            </ProcessorTableCell>
            <ProcessorTableCell sx={{ minWidth: cellWidth }}>
              {t(`${COMPONENT_LOCALE_KEY}.columns.foreignMdr`)}
            </ProcessorTableCell>
            {/* <ProcessorTableCell sx={{ minWidth: cellWidth }}>
              {t(`${COMPONENT_LOCALE_KEY}.columns.referrerCommission`)}
            </ProcessorTableCell> */}
            <ProcessorTableCell sx={{ minWidth: wideCellWidth }}>
              {t(`${COMPONENT_LOCALE_KEY}.columns.inUse`)}
            </ProcessorTableCell>
          </TableRow>
        </TableHead>
        <TableBody sx={{ color: c.black }}>
          <TableRow>
            <ProcessorTableCell sx={{ height: "60px" }}>
              <img
                src={getCardTypeIcon(config.cardTypeKey)}
                height={36}
              />
            </ProcessorTableCell>
            <ProcessorTableCell>
              {config.unavailableFields?.includes("domesticMdr") ? <UnavailablePlaceholder /> : FormBuilder.buildFields({ formProps, fields: [tableFields.domesticMdr] })}
            </ProcessorTableCell>
            <ProcessorTableCell>
              {config.unavailableFields?.includes("foreignMdr") ? <UnavailablePlaceholder /> : FormBuilder.buildFields({ formProps, fields: [tableFields.foreignMdr] })}
            </ProcessorTableCell>
            {/* <ProcessorTableCell>
              {config.unavailableFields?.includes("referrerCommission") ? <UnavailablePlaceholder /> : FormBuilder.buildFields({ formProps, fields: [tableFields.referrerCommission] })}
            </ProcessorTableCell> */}
            <ProcessorTableCell>
              {config.unavailableFields?.includes("inUse") ? <UnavailablePlaceholder /> : FormBuilder.buildFields({ formProps, fields: [tableFields.inUse] })}
            </ProcessorTableCell>
          </TableRow>

          <TableRow sx={{ backgroundColor: c.brandBluegrey1 }}>
            <ProcessorTableCell>
              {t(`${COMPONENT_LOCALE_KEY}.columns.gateway`)}
            </ProcessorTableCell>
            <ProcessorTableCell>
              {t(`${COMPONENT_LOCALE_KEY}.columns.businessPlan`)}
            </ProcessorTableCell>
            <ProcessorTableCell>
              {t(`${COMPONENT_LOCALE_KEY}.columns.enabled`)}
            </ProcessorTableCell>
            <ProcessorTableCell>
              {t(`${COMPONENT_LOCALE_KEY}.columns.mcc`)}
            </ProcessorTableCell>
          </TableRow>
          {
            config.gateways.map((gateway, index) => {
              const gatewayFormKey = `${formKey}.gateways.${gateway.gatewayKey}`;
              const unavailableFields = gateway.unavailableFields ?? [];

              /** @type {Record<string, import("components/FormBuilder/lib/FormBuilderFieldBuilder").formBuilderFieldConfig>} */
              const gatewayFields = {
                businessPlan: {
                  fieldType: "selection",
                  fieldName: `${gatewayFormKey}.businessPlan`,
                  hideLabel: "removePadding",
                  noErrorMessage: true,
                  mandatory: true,
                  disabled: disabled,
                  config: {
                    defaultValue: gateway.defaultValues?.businessPlan,
                    options: BUSINESS_PLAN_OPTIONS,
                  }
                },
                enabled: {
                  fieldType: "checkbox",
                  fieldName: `${gatewayFormKey}.enabled`,
                  hideLabel: "removePadding",
                  disabled: disabled,
                  config: {
                    defaultValue: gateway.defaultValues?.enabled,
                    defaultValueTriggerUpdate: true,
                    customOnCheckFn: (value, formProps) => {
                      const { setValue, getValues } = formProps;
                      // if (value && !getValues(`${formKey}.inUse`)) setValue(`${formKey}.inUse`, gateway.gatewayKey);
                      if (!value && getValues(`${formKey}.inUse`) === gateway.gatewayKey) setValue(`${formKey}.inUse`, GATEWAY_INUSE_NONE_VALUE);
                      gateway.specialHandling?.enabledOnCheckFn && gateway.specialHandling.enabledOnCheckFn(value, formProps);
                    }
                  }
                },
                mcc: {
                  fieldType: "selection",
                  fieldName: `${gatewayFormKey}.mcc`,
                  hideLabel: "removePadding",
                  noErrorMessage: true,
                  mandatory: true,
                  disabled: disabled,
                  config: {
                    defaultValue: gateway.defaultValues?.mcc ?? defaultValues.mcc,
                    options: mccOptions ?? [],
                  }
                }
              }

              return (
                <TableRow key={`${gateway.gatewayKey}-${index}`}>
                  <ProcessorTableCell>
                    {gateway.gatewayKey}
                  </ProcessorTableCell>
                  <ProcessorTableCell>
                    {unavailableFields.includes("businessPlan") ? <UnavailablePlaceholder /> : FormBuilder.buildFields({ formProps, fields: [gatewayFields.businessPlan] })}
                  </ProcessorTableCell>
                  <ProcessorTableCell>
                    {unavailableFields.includes("enabled") ? <UnavailablePlaceholder /> : FormBuilder.buildFields({ formProps, fields: [gatewayFields.enabled] })}
                  </ProcessorTableCell>
                  <ProcessorTableCell>
                    {unavailableFields.includes("mcc") ? <UnavailablePlaceholder /> : FormBuilder.buildFields({ formProps, fields: [gatewayFields.mcc] })}
                  </ProcessorTableCell>
                </TableRow>
              )
            })
          }
        </TableBody>
      </Table>
    </TableContainer>
  )
}

export default React.memo(ProcessorTable);
